import type {
  EventDetails,
  ExternalRegistrationDetails,
  JobteaserRegistrationDetails,
  RegistrationStatus,
} from "@/modules/events/types/eventDetailsApi"
import type { FunctionComponent } from "react"

import { differenceInCalendarDays, differenceInHours, differenceInMinutes, isBefore } from "date-fns"
import { useParams } from "next/navigation"

import { Callout } from "@jobteaser/spark/components/Callout"

import { REGISTRATION_STATUS_SUBSCRIBED, WITHOUT_REGISTRATION } from "@/modules/events/constants"
import { useEventDetailsData } from "@/modules/events/hooks/useEventDetailsData"
import { useTranslation } from "@/modules/i18n/components/useTranslation"

import styles from "./UnregistrationReminder.module.css"

type UnregistrationReminderProps = {
  registrationStatus: RegistrationStatus
}

export const UnregistrationReminder: FunctionComponent<UnregistrationReminderProps> = ({ registrationStatus }) => {
  const { t } = useTranslation(["event_details"])
  const { eventSlug } = useParams<{ eventSlug: string }>()
  const event = useEventDetailsData(eventSlug)

  if (!event) {
    return null
  }

  const { registrationDetails } = event as EventDetails

  if (
    registrationDetails.type === WITHOUT_REGISTRATION ||
    registrationStatus.status !== REGISTRATION_STATUS_SUBSCRIBED
  ) {
    return null
  }

  const currentDate = new Date()
  const unregistrationDeadline = new Date(
    (registrationDetails as JobteaserRegistrationDetails | ExternalRegistrationDetails).unregistrationDeadline
  )

  const daysUntilUnregistrationEnds = differenceInCalendarDays(unregistrationDeadline, currentDate)
  const hoursUntilUnregistrationEnds = differenceInHours(unregistrationDeadline, currentDate)
  const minutesUntilUnregistrationEnds = differenceInMinutes(unregistrationDeadline, currentDate, {
    roundingMethod: "ceil",
  })

  const FOURTEEN_DAYS = 14
  const ONE_DAY = 1
  const TWENTY_FOUR_HOURS = 24
  const TWO_HOURS = 2
  const ONE_HOUR = 1
  const ONE_MINUTE = 1

  const getReminderText = (): string => {
    if (isBefore(currentDate, unregistrationDeadline)) {
      if (daysUntilUnregistrationEnds > FOURTEEN_DAYS) {
        return ""
      }
      if (daysUntilUnregistrationEnds >= ONE_DAY && hoursUntilUnregistrationEnds >= TWENTY_FOUR_HOURS) {
        return t("event_details.unregistration.closing.day", { count: daysUntilUnregistrationEnds })
      }
      if (hoursUntilUnregistrationEnds < TWENTY_FOUR_HOURS && hoursUntilUnregistrationEnds >= TWO_HOURS) {
        return t("event_details.unregistration.closing.hour", { count: hoursUntilUnregistrationEnds })
      }
      if (hoursUntilUnregistrationEnds < TWO_HOURS && hoursUntilUnregistrationEnds >= ONE_HOUR) {
        return t("event_details.unregistration.closing.hour", { count: ONE_HOUR })
      }
      if (hoursUntilUnregistrationEnds < ONE_HOUR && minutesUntilUnregistrationEnds > ONE_MINUTE) {
        return t("event_details.unregistration.closing.minute", { count: minutesUntilUnregistrationEnds })
      }
      if (minutesUntilUnregistrationEnds === ONE_MINUTE) {
        return t("event_details.unregistration.closing.minute", { count: minutesUntilUnregistrationEnds })
      }
    }
    return ""
  }

  const text = getReminderText()

  if (text === "") {
    return null
  }

  return (
    <Callout className={styles.main} data-testid="event-details-registration-details-unregistration-reminder">
      {text}
    </Callout>
  )
}
